import React, { createContext, useContext, useMemo } from "react";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { IssueRow } from "#app/lib/data";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import type { CardDisplayOptions, OnCreateOption } from "./issue-card-fields";
import { useCardLabelRail } from "./card-label-rail";

export type IssueCardActions = {
  onAddressSelect: (issue: IssueRow, wholeAddress: string) => void;
  onAddressClear: (issue: IssueRow) => void;
  onFieldValuesChange: (
    issue: IssueRow,
    field: CustomFieldDef,
    value: unknown,
  ) => void;
  onStageChange: (issue: IssueRow, stageId: string) => void;
  onProjectTypeChange: (issue: IssueRow, issueTypeId: string) => void;
  onTeamsChange: (issue: IssueRow, teamIds: string[]) => void;
  onCreateOption?: OnCreateOption | undefined;
  optionActions?: OptionActions | undefined;
};

type IssueCardProviderProps = {
  actions: IssueCardActions;
  /** The fields this view SHOWS on its cards (already filtered by
   *  `shownFieldIds`). */
  customFieldDefs: CustomFieldDef[];
  displayOptions: CardDisplayOptions;
};

type IssueCardContextValue = IssueCardProviderProps & {
  /** Text-mode label rail, px — one measurement per view, shared by every
   *  card so their values line up (`card-label-rail.ts`). */
  labelRail: number;
};

const IssueCardContext = createContext<IssueCardContextValue | null>(null);

/** Names the view's cards can label, properties by their display flags and
 *  fields by the shown set — independent of any one card's VALUES, so every
 *  card measures the same rail. Property names match the card's own labels
 *  (`IssueCardAddress`, `IssueCardTeams`, `IssueCardExtraProperty`). */
function cardLabelNames(
  displayOptions: CardDisplayOptions,
  customFieldDefs: CustomFieldDef[],
): string[] {
  const names: string[] = [];
  if (displayOptions.showAddress) names.push("Address");
  if (displayOptions.showTeams) names.push("Teams");
  if (displayOptions.showStage) names.push("Stage");
  if (displayOptions.showWorkflow) names.push("Workflow");
  if (displayOptions.showProjectType) names.push("Project Type");
  if (displayOptions.showCreated) names.push("Created");
  if (displayOptions.showUpdated) names.push("Updated");
  for (const field of customFieldDefs) names.push(field.name);
  return names;
}

export function IssueCardProvider({
  actions,
  customFieldDefs,
  displayOptions,
  children,
}: IssueCardProviderProps & { children: React.ReactNode }) {
  const labelRail = useCardLabelRail(
    cardLabelNames(displayOptions, customFieldDefs),
  );
  const value = useMemo(
    () => ({ actions, customFieldDefs, displayOptions, labelRail }),
    [actions, customFieldDefs, displayOptions, labelRail],
  );
  return (
    <IssueCardContext.Provider value={value}>
      {children}
    </IssueCardContext.Provider>
  );
}

export function useIssueCardContext(): IssueCardContextValue {
  const value = useContext(IssueCardContext);
  if (!value) {
    throw new Error("IssueCard must render inside IssueCardProvider");
  }
  return value;
}
